import { KnowledgeItem } from '../../types/learning';
import { shuffleArray, interleaveArrays } from '../../utils/arrayUtils';
import { addDays } from 'date-fns';

/**
 * Configuration options for the interleave scheduler
 */
export interface InterleaveOptions {
  /** Percentage of coordinate challenges (0-1) */
  coordinateRatio: number;
  /** Percentage of move challenges (0-1) */
  moveRatio: number;
  /** Percentage of sequence challenges (0-1) */
  sequenceRatio: number;
  /** Whether to include unseen items */
  includeNewContent: boolean;
  /** Total items to include in session */
  sessionLength: number;
}

/**
 * Weights used for prioritizing items in the selection algorithm
 */
interface ItemPriorityWeights {
  /** Weight for items that are due for review (higher = more priority) */
  dueWeight: number;
  /** Weight for items that have been difficult (higher = more priority) */
  difficultyWeight: number;
  /** Weight for elapsed time since last review (higher = more priority) */
  elapsedTimeWeight: number;
}

/** 
 * Default weights used for prioritizing items in the selection algorithm.
 * - `dueWeight`: Higher values give more priority to items overdue for review.
 * - `difficultyWeight`: Higher values give more priority to items that have been historically difficult (low eFactor).
 * - `elapsedTimeWeight`: Higher values give more priority to items not reviewed recently.
 */
const DEFAULT_PRIORITY_WEIGHTS: ItemPriorityWeights = {
  dueWeight: 3.0,
  difficultyWeight: 2.0,
  elapsedTimeWeight: 1.0
};

/**
 * Creates a mixed practice session with balanced item types following the SM-2 spaced repetition approach
 * 
 * @param knowledgeItems All available knowledge items
 * @param options Configuration for session generation
 * @returns Ordered array of items for practice
 */
export function createInterleaveSession(
  knowledgeItems: KnowledgeItem[],
  options: InterleaveOptions
): KnowledgeItem[] {
  // 1. Split items by type
  const coordinateItems = knowledgeItems.filter(item => item.type === 'coordinate');
  const moveItems = knowledgeItems.filter(item => item.type === 'move');
  const sequenceItems = knowledgeItems.filter(item => item.type === 'sequence');
  
  // 2. Filter items based on review status
  const now = new Date();
  
  const dueCoordinateItems = coordinateItems.filter(item => 
    isDueForReview(item, now) || (options.includeNewContent && item.repetition === 0)
  );
  
  const dueMoveItems = moveItems.filter(item => 
    isDueForReview(item, now) || (options.includeNewContent && item.repetition === 0)
  );
  
  const dueSequenceItems = sequenceItems.filter(item => 
    isDueForReview(item, now) || (options.includeNewContent && item.repetition === 0)
  );
  
  // 3. Calculate item counts based on ratios
  const totalItemCount = Math.min(options.sessionLength, 
    dueCoordinateItems.length + dueMoveItems.length + dueSequenceItems.length);
  
  if (totalItemCount === 0) {
    return []; // No items to review
  }
  
  // Normalize ratios if they don't sum to 1
  const sum = options.coordinateRatio + options.moveRatio + options.sequenceRatio;
  const normalizedCoordinateRatio = options.coordinateRatio / sum;
  const normalizedMoveRatio = options.moveRatio / sum;
  const normalizedSequenceRatio = options.sequenceRatio / sum;
  
  // Calculate target counts
  let targetCoordinateCount = Math.round(totalItemCount * normalizedCoordinateRatio);
  let targetMoveCount = Math.round(totalItemCount * normalizedMoveRatio);
  let targetSequenceCount = Math.round(totalItemCount * normalizedSequenceRatio);
  
  // Adjust counts if we have fewer items than target
  targetCoordinateCount = Math.min(targetCoordinateCount, dueCoordinateItems.length);
  targetMoveCount = Math.min(targetMoveCount, dueMoveItems.length);
  targetSequenceCount = Math.min(targetSequenceCount, dueSequenceItems.length);
  
  // Calculate remaining slots and redistribute if necessary
  const allocatedCount = targetCoordinateCount + targetMoveCount + targetSequenceCount;
  const remainingSlots = totalItemCount - allocatedCount;
  
  if (remainingSlots > 0) {
    // Redistribute remaining slots based on availability and priority
    const redistributed = redistributeRemainingSlots(
      remainingSlots,
      [dueCoordinateItems.length - targetCoordinateCount, dueMoveItems.length - targetMoveCount, dueSequenceItems.length - targetSequenceCount],
      [normalizedCoordinateRatio, normalizedMoveRatio, normalizedSequenceRatio]
    );
    
    targetCoordinateCount += redistributed[0];
    targetMoveCount += redistributed[1];
    targetSequenceCount += redistributed[2];
  }
  
  // 4. Select items with prioritization logic
  const selectedCoordinates = selectItemsWithPriority(dueCoordinateItems, targetCoordinateCount, DEFAULT_PRIORITY_WEIGHTS);
  const selectedMoves = selectItemsWithPriority(dueMoveItems, targetMoveCount, DEFAULT_PRIORITY_WEIGHTS);
  const selectedSequences = selectItemsWithPriority(dueSequenceItems, targetSequenceCount, DEFAULT_PRIORITY_WEIGHTS);
  
  // 5. Interleave items in optimal order with controlled distribution
  return interleaveArrays(selectedCoordinates, selectedMoves, selectedSequences);
}

/**
 * Determines if an item is due for review based on its next review date
 */
function isDueForReview(item: KnowledgeItem, currentDate: Date): boolean {
  return item.repetition > 0 && item.nextReviewDate <= currentDate;
}

/**
 * Calculates a priority score for an item based on various factors
 */
function calculateItemPriority(item: KnowledgeItem, weights: ItemPriorityWeights): number {
  const now = new Date();
  
  // Due factor: how overdue is this item (capped at 30 days)
  const daysOverdue = Math.max(0, Math.min(30, 
    (now.getTime() - item.nextReviewDate.getTime()) / (1000 * 60 * 60 * 24)
  ));
  const dueFactor = daysOverdue * weights.dueWeight;
  
  // Difficulty factor: lower efactor means more difficult
  const difficultyFactor = (2.5 - item.efactor) * 2 * weights.difficultyWeight;
  
  // Elapsed time factor: items not seen in a long time get priority
  const daysSinceLastReview = Math.min(90, 
    (now.getTime() - item.lastReviewed.getTime()) / (1000 * 60 * 60 * 24)
  );
  const elapsedTimeFactor = daysSinceLastReview * 0.1 * weights.elapsedTimeWeight;
  
  // Combined priority score
  return dueFactor + difficultyFactor + elapsedTimeFactor;
}

/**
 * Selects items based on priority, with some randomization to prevent predictability
 */
function selectItemsWithPriority(
  items: KnowledgeItem[], 
  count: number, 
  weights: ItemPriorityWeights
): KnowledgeItem[] {
  if (count >= items.length) {
    return shuffleArray(items); // Return all items, shuffled
  }
  
  // Calculate priority scores for all items
  const itemsWithPriority = items.map(item => ({
    item,
    priority: calculateItemPriority(item, weights)
  }));
  
  // Sort by priority (highest first)
  itemsWithPriority.sort((a, b) => b.priority - a.priority);
  
  // Take the top 70% of needed items by priority
  const topCount = Math.max(1, Math.floor(count * 0.7));
  const topItems = itemsWithPriority.slice(0, topCount).map(ip => ip.item);
  
  // Take the remaining 30% with some randomization
  const remainingItems = itemsWithPriority.slice(topCount).map(ip => ip.item);
  const remainingCount = count - topCount;
  
  if (remainingCount <= 0 || remainingItems.length === 0) {
    return topItems;
  }
  
  // Shuffle the remaining items and take what we need
  const shuffledRemaining = shuffleArray(remainingItems).slice(0, remainingCount);
  
  // Combine and shuffle again to mix priority and random selections
  return shuffleArray([...topItems, ...shuffledRemaining]);
}

/**
 * Redistributes remaining slots based on availability and priority
 */
function redistributeRemainingSlots(
  remainingSlots: number,
  availableCounts: number[],
  priorities: number[]
): number[] {
  const result = [0, 0, 0];
  
  // If no slots to redistribute or nothing available, return zeros
  if (remainingSlots <= 0 || availableCounts.every(count => count <= 0)) {
    return result;
  }
  
  // Create weighted distribution based on priorities and availability
  const weightedAvailability = availableCounts.map((count, i) => 
    count > 0 ? priorities[i] : 0
  );
  
  const totalWeight = weightedAvailability.reduce((sum, weight) => sum + weight, 0);
  
  if (totalWeight === 0) {
    return result;
  }
  
  // First pass: distribute based on weighted priority
  const initialDistribution = weightedAvailability.map(weight => 
    Math.floor((weight / totalWeight) * remainingSlots)
  );
  
  // Calculate how many we've assigned in the first pass
  const initialTotal = initialDistribution.reduce((sum, count) => sum + count, 0);
  
  // Distribute any remaining slots one by one to the highest priority categories
  let remaining = remainingSlots - initialTotal;
  const combinedWeights = weightedAvailability.map((weight, i) => ({ 
    index: i, 
    weight, 
    availability: availableCounts[i] - initialDistribution[i]
  }))
  .filter(item => item.availability > 0)
  .sort((a, b) => b.weight - a.weight);
  
  // Distribute remaining slots to highest priority first
  let index = 0;
  while (remaining > 0 && combinedWeights.length > 0) {
    const targetIndex = combinedWeights[index % combinedWeights.length].index;
    if (result[targetIndex] < availableCounts[targetIndex]) {
      result[targetIndex]++;
      remaining--;
    }
    index++;
    
    // If we've gone through all options, break to avoid infinite loop
    if (index >= combinedWeights.length * 2) break;
  }
  
  // Add the initial distribution
  for (let i = 0; i < 3; i++) {
    result[i] += initialDistribution[i];
  }
  
  return result;
} 